import type { AuthClient } from '@/utils/auth-client';
import type { OrderAndInvoiceLineDailyRevenue } from '@plato/data/starrocks/types';
import { SrOrderLineStatus, SrSalesOrderType } from '@plato/data/starrocks/types';
import {
    endOfDay,
    endOfToday,
    endOfYear,
    startOfDay,
    startOfToday,
    startOfYear,
    subDays,
    subMonths,
    subQuarters,
    subWeeks,
    subYears,
} from 'date-fns';
import { sql } from 'kysely';
import { z } from 'zod';

export type ExplorerViewHandles = {
    fetchNextPage: () => void;
};

export const valueInStringEnum = (stringEnum: any, value: any): boolean => {
    return Object.values(stringEnum).includes(value);
};

export enum ExplorerSortOrder {
    ASC = 'asc',
    DESC = 'desc',
}

export enum TargetUnit {
    REVENUE = 'revenue',
    UNITS = 'units',
}

export enum ExplorerSortBy {
    ID = 'id',
    NAME = 'name',
    REVENUE_CURRENT = 'revenue_current',
    REVENUE_LAST_PERIOD = 'revenue_last_period',
    REVENUE_PERIOD_3 = 'revenue_period_3',
    REVENUE_PERIOD_4 = 'revenue_period_4',
    REVENUE_PERIOD_5 = 'revenue_period_5',
    REVENUE_DELTA_AMOUNT = 'revenue_delta_amount',
    REVENUE_DELTA_PERCENTAGE = 'revenue_delta_percentage',
    GROSS_MARGIN_CURRENT = 'gross_margin_current',
    GROSS_MARGIN_LAST_PERIOD = 'gross_margin_last_period',
    GROSS_MARGIN_PERIOD_3 = 'gross_margin_period_3',
    GROSS_MARGIN_PERIOD_4 = 'gross_margin_period_4',
    GROSS_MARGIN_PERIOD_5 = 'gross_margin_period_5',
    GROSS_MARGIN_DELTA_AMOUNT = 'gross_margin_delta_amount',
    GROSS_MARGIN_DELTA_PERCENTAGE = 'gross_margin_delta_percentage',
    GROSS_MARGIN_PERCENTAGE_CURRENT = 'gross_margin_percentage_current',
    GROSS_MARGIN_PERCENTAGE_LAST_PERIOD = 'gross_margin_percentage_last_period',
    GROSS_MARGIN_PERCENTAGE_PERIOD_3 = 'gross_margin_percentage_period_3',
    GROSS_MARGIN_PERCENTAGE_PERIOD_4 = 'gross_margin_percentage_period_4',
    GROSS_MARGIN_PERCENTAGE_PERIOD_5 = 'gross_margin_percentage_period_5',
    UNITS_CURRENT = 'units_current',
    UNITS_LAST_PERIOD = 'units_last_period',
    UNITS_DELTA_AMOUNT = 'units_delta_amount',
    UNITS_DELTA_PERCENTAGE = 'units_delta_percentage',
}

export const mapToTargetUnit: Record<TargetUnit, Record<ExplorerSortBy, ExplorerSortBy>> = {
    [TargetUnit.REVENUE]: {
        [ExplorerSortBy.ID]: ExplorerSortBy.ID,
        [ExplorerSortBy.NAME]: ExplorerSortBy.NAME,
        [ExplorerSortBy.REVENUE_CURRENT]: ExplorerSortBy.REVENUE_CURRENT,
        [ExplorerSortBy.REVENUE_LAST_PERIOD]: ExplorerSortBy.REVENUE_LAST_PERIOD,
        [ExplorerSortBy.REVENUE_PERIOD_3]: ExplorerSortBy.REVENUE_PERIOD_3,
        [ExplorerSortBy.REVENUE_PERIOD_4]: ExplorerSortBy.REVENUE_PERIOD_4,
        [ExplorerSortBy.REVENUE_PERIOD_5]: ExplorerSortBy.REVENUE_PERIOD_5,
        [ExplorerSortBy.REVENUE_DELTA_AMOUNT]: ExplorerSortBy.REVENUE_DELTA_AMOUNT,
        [ExplorerSortBy.REVENUE_DELTA_PERCENTAGE]: ExplorerSortBy.REVENUE_DELTA_PERCENTAGE,
        [ExplorerSortBy.GROSS_MARGIN_CURRENT]: ExplorerSortBy.GROSS_MARGIN_CURRENT,
        [ExplorerSortBy.GROSS_MARGIN_LAST_PERIOD]: ExplorerSortBy.GROSS_MARGIN_LAST_PERIOD,
        [ExplorerSortBy.GROSS_MARGIN_PERIOD_3]: ExplorerSortBy.GROSS_MARGIN_PERIOD_3,
        [ExplorerSortBy.GROSS_MARGIN_PERIOD_4]: ExplorerSortBy.GROSS_MARGIN_PERIOD_4,
        [ExplorerSortBy.GROSS_MARGIN_PERIOD_5]: ExplorerSortBy.GROSS_MARGIN_PERIOD_5,
        [ExplorerSortBy.GROSS_MARGIN_DELTA_AMOUNT]: ExplorerSortBy.GROSS_MARGIN_DELTA_AMOUNT,
        [ExplorerSortBy.GROSS_MARGIN_DELTA_PERCENTAGE]: ExplorerSortBy.GROSS_MARGIN_DELTA_PERCENTAGE,
        [ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_CURRENT]: ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_CURRENT,
        [ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_LAST_PERIOD]:
            ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_LAST_PERIOD,
        [ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_PERIOD_3]: ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_PERIOD_3,
        [ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_PERIOD_4]: ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_PERIOD_4,
        [ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_PERIOD_5]: ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_PERIOD_5,
        [ExplorerSortBy.UNITS_CURRENT]: ExplorerSortBy.REVENUE_CURRENT,
        [ExplorerSortBy.UNITS_LAST_PERIOD]: ExplorerSortBy.REVENUE_LAST_PERIOD,
        [ExplorerSortBy.UNITS_DELTA_AMOUNT]: ExplorerSortBy.REVENUE_DELTA_AMOUNT,
        [ExplorerSortBy.UNITS_DELTA_PERCENTAGE]: ExplorerSortBy.REVENUE_DELTA_PERCENTAGE,
    },
    [TargetUnit.UNITS]: {
        [ExplorerSortBy.ID]: ExplorerSortBy.ID,
        [ExplorerSortBy.NAME]: ExplorerSortBy.NAME,
        [ExplorerSortBy.REVENUE_CURRENT]: ExplorerSortBy.UNITS_CURRENT,
        [ExplorerSortBy.REVENUE_LAST_PERIOD]: ExplorerSortBy.UNITS_LAST_PERIOD,
        [ExplorerSortBy.REVENUE_PERIOD_3]: ExplorerSortBy.REVENUE_CURRENT,
        [ExplorerSortBy.REVENUE_PERIOD_4]: ExplorerSortBy.REVENUE_CURRENT,
        [ExplorerSortBy.REVENUE_PERIOD_5]: ExplorerSortBy.REVENUE_CURRENT,
        [ExplorerSortBy.GROSS_MARGIN_CURRENT]: ExplorerSortBy.UNITS_CURRENT,
        [ExplorerSortBy.GROSS_MARGIN_LAST_PERIOD]: ExplorerSortBy.UNITS_LAST_PERIOD,
        [ExplorerSortBy.GROSS_MARGIN_PERIOD_3]: ExplorerSortBy.UNITS_CURRENT,
        [ExplorerSortBy.GROSS_MARGIN_PERIOD_4]: ExplorerSortBy.UNITS_CURRENT,
        [ExplorerSortBy.GROSS_MARGIN_PERIOD_5]: ExplorerSortBy.UNITS_CURRENT,
        [ExplorerSortBy.REVENUE_DELTA_AMOUNT]: ExplorerSortBy.UNITS_DELTA_AMOUNT,
        [ExplorerSortBy.REVENUE_DELTA_PERCENTAGE]: ExplorerSortBy.UNITS_DELTA_PERCENTAGE,
        [ExplorerSortBy.GROSS_MARGIN_DELTA_AMOUNT]: ExplorerSortBy.UNITS_DELTA_AMOUNT,
        [ExplorerSortBy.GROSS_MARGIN_DELTA_PERCENTAGE]: ExplorerSortBy.UNITS_DELTA_PERCENTAGE,
        [ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_CURRENT]: ExplorerSortBy.UNITS_CURRENT,
        [ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_LAST_PERIOD]: ExplorerSortBy.UNITS_LAST_PERIOD,
        [ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_PERIOD_3]: ExplorerSortBy.UNITS_CURRENT,
        [ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_PERIOD_4]: ExplorerSortBy.UNITS_CURRENT,
        [ExplorerSortBy.GROSS_MARGIN_PERCENTAGE_PERIOD_5]: ExplorerSortBy.UNITS_CURRENT,
        [ExplorerSortBy.UNITS_CURRENT]: ExplorerSortBy.UNITS_CURRENT,
        [ExplorerSortBy.UNITS_LAST_PERIOD]: ExplorerSortBy.UNITS_LAST_PERIOD,
        [ExplorerSortBy.UNITS_DELTA_AMOUNT]: ExplorerSortBy.UNITS_DELTA_AMOUNT,
        [ExplorerSortBy.UNITS_DELTA_PERCENTAGE]: ExplorerSortBy.UNITS_DELTA_PERCENTAGE,
    },
};

export const validSorts = Object.values(ExplorerSortBy);

export const explorerSortSchema = z
    .object({
        field: z.nativeEnum(ExplorerSortBy),
        direction: z.nativeEnum(ExplorerSortOrder),
    })
    .optional()
    .nullable();

export type ExplorerSort = z.infer<typeof explorerSortSchema>;

export const explorerViewFiltersSchema = z.object({
    customer: z.array(z.string()),
    clerk: z.array(z.string()),
    industry: z.array(z.string()),
    product: z.array(z.string()),
    productGroup: z.array(z.string()),
    storageLocation: z.array(z.string()),
    supplier: z.array(z.string()),
    salesRep: z.array(z.string()),
    normalisedCategory: z.array(z.string()).optional().default([]),
});

export type ExplorerViewFilters = z.infer<typeof explorerViewFiltersSchema>;

export const emptyExplorerViewFilters = {
    customer: [],
    clerk: [],
    industry: [],
    product: [],
    productGroup: [],
    storageLocation: [],
    supplier: [],
    salesRep: [],
    normalisedCategory: [],
};

export type DefaultExplorerViewFilter = {
    id: string;
    canBeRemoved: boolean;
};

export type DefaultExplorerViewFilters = {
    customer?: DefaultExplorerViewFilter[];
    clerk?: DefaultExplorerViewFilter[];
    industry?: DefaultExplorerViewFilter[];
    product?: DefaultExplorerViewFilter[];
    productGroup?: DefaultExplorerViewFilter[];
    storageLocation?: DefaultExplorerViewFilter[];
    supplier?: DefaultExplorerViewFilter[];
    salesRep?: DefaultExplorerViewFilter[];
    normalisedCategory?: DefaultExplorerViewFilter[];
};

export enum ExplorerTopLevelEntity {
    STORAGE_LOCATION = 'STORAGE_LOCATION',
    CLERK = 'CLERK',
    SALES_REP = 'SALES_REP',
    CUSTOMER = 'CUSTOMER',
    INDUSTRY = 'INDUSTRY',
    PRODUCT = 'PRODUCT',
    PRODUCT_GROUP = 'PRODUCT_GROUP',
    SUPPLIER = 'SUPPLIER',
    NORMALISED_CATEGORY = 'NORMALISED_CATEGORY',
}

export const getExplorerFilterKeyByTabName = (tab: ExplorerTopLevelEntity): keyof ExplorerViewFilters => {
    switch (tab) {
        case ExplorerTopLevelEntity.CUSTOMER:
            return 'customer';
        case ExplorerTopLevelEntity.CLERK:
            return 'clerk';
        case ExplorerTopLevelEntity.INDUSTRY:
            return 'industry';
        case ExplorerTopLevelEntity.PRODUCT:
            return 'product';
        case ExplorerTopLevelEntity.PRODUCT_GROUP:
            return 'productGroup';
        case ExplorerTopLevelEntity.NORMALISED_CATEGORY:
            return 'normalisedCategory';
        case ExplorerTopLevelEntity.STORAGE_LOCATION:
            return 'storageLocation';
        case ExplorerTopLevelEntity.SUPPLIER:
            return 'supplier';
        case ExplorerTopLevelEntity.SALES_REP:
            return 'salesRep';
        default:
            return 'customer';
    }
};

export const allExplorerTabs = [
    ExplorerTopLevelEntity.STORAGE_LOCATION,
    ExplorerTopLevelEntity.CLERK,
    ExplorerTopLevelEntity.SALES_REP,
    ExplorerTopLevelEntity.CUSTOMER,
    ExplorerTopLevelEntity.INDUSTRY,
    ExplorerTopLevelEntity.NORMALISED_CATEGORY,
    ExplorerTopLevelEntity.PRODUCT_GROUP,
    ExplorerTopLevelEntity.PRODUCT,
    ExplorerTopLevelEntity.SUPPLIER,
];

export enum VisibilityWindow {
    TODAY = 'TODAY',
    YESTERDAY = 'YESTERDAY',
    LAST_WEEK = 'LAST_WEEK',
    LAST_MONTH = 'LAST_MONTH',
    LAST_QUARTER = 'LAST_QUARTER',
    TTM = 'TTM',
    YTD = 'YTD',
    FULL_YEAR = 'FULL_YEAR',
    CUSTOM_DATES = 'CUSTOM_DATES',
}

export type CustomDateRange = {
    from: Date;
    to: Date;
};

export const dateRangeLookup = {
    [VisibilityWindow.TODAY]: () => ({
        from: startOfToday(),
        to: endOfToday(),
    }),
    [VisibilityWindow.YESTERDAY]: () => {
        const today = startOfToday();
        return {
            from: startOfDay(subDays(today, 1)),
            to: endOfDay(subDays(today, 1)),
        };
    },
    [VisibilityWindow.LAST_WEEK]: () => ({
        from: startOfDay(subWeeks(startOfToday(), 1)),
        to: endOfToday(),
    }),
    [VisibilityWindow.LAST_MONTH]: () => ({
        from: startOfDay(subMonths(startOfToday(), 1)),
        to: endOfToday(),
    }),
    [VisibilityWindow.LAST_QUARTER]: () => ({
        from: startOfDay(subQuarters(startOfToday(), 1)),
        to: endOfToday(),
    }),
    [VisibilityWindow.TTM]: () => ({
        from: startOfDay(subYears(startOfToday(), 1)),
        to: endOfToday(),
    }),
    [VisibilityWindow.YTD]: () => ({
        from: startOfYear(startOfToday()),
        to: endOfToday(),
    }),
    [VisibilityWindow.FULL_YEAR]: () => {
        const today = startOfToday();
        return {
            from: startOfYear(today),
            to: endOfYear(today),
        };
    },
    [VisibilityWindow.CUSTOM_DATES]: () => null,
};

export const getDateRange = (
    visibilityWindow: VisibilityWindow,
    customDateRange?: CustomDateRange | null,
) => {
    if (visibilityWindow === VisibilityWindow.CUSTOM_DATES && customDateRange) {
        return {
            ...customDateRange,
        };
    }

    const rangeFunction = dateRangeLookup[visibilityWindow];

    if (!rangeFunction) {
        return null;
    }

    return rangeFunction();
};

export const getDateRangeOrThrow = (
    visibilityWindow: VisibilityWindow,
    customDateRange?: CustomDateRange | undefined | null,
): { from: Date; to: Date } => {
    if (visibilityWindow === VisibilityWindow.CUSTOM_DATES && customDateRange) {
        return {
            ...customDateRange,
        };
    } else if (visibilityWindow === VisibilityWindow.CUSTOM_DATES && !customDateRange) {
        throw new Error('Custom date range is required for CUSTOM_DATES visibility window.');
    }

    const rangeFunction = dateRangeLookup[visibilityWindow];

    if (!rangeFunction) {
        throw new Error('Invalid visibility window.');
    }

    const result = rangeFunction();

    if (!result || !result.from || !result.to) {
        throw new Error('Invalid date range.');
    }

    return result;
};

export enum BillingStatus {
    BILLED = 'BILLED',
    BILLED_WO_CREDIT_NOTE = 'BILLED_WO_CREDIT_NOTE',
    NOT_BILLED = 'NOT_BILLED',
}

// Valid intervals are 2, 3, 4 (representing periods 3, 4, 5)
export type YearPeriodInterval = 2 | 3 | 4;
export type YearPeriodNumber = 3 | 4 | 5;

export type RevenueYearPeriodKey = `revenue_period_${YearPeriodNumber}`;
export type GrossMarginYearPeriodKey = `gross_margin_period_${YearPeriodNumber}`;
export type GrossMarginPercentageYearPeriodKey = `gross_margin_percentage_period_${YearPeriodNumber}`;

export const yearPeriodsHelper = {
    max: 5,
    min: 2,
    // value should be between 2 and 5
    getValidPeriods: (yearPeriods: number) =>
        Math.max(yearPeriodsHelper.min, Math.min(yearPeriods, yearPeriodsHelper.max)),
    getIntervals: (yearPeriods: number): YearPeriodInterval[] => {
        const yearPeriodsLength = yearPeriodsHelper.getValidPeriods(yearPeriods);
        return Array.from({
            length: yearPeriodsLength - yearPeriodsHelper.min,
        }).map((_, i) => (i + yearPeriodsHelper.min) as YearPeriodInterval);
    },
    // Revenue columns are named revenue_period_3, revenue_period_4, etc.
    getRevenueColumnNameByInterval: (interval: YearPeriodInterval): RevenueYearPeriodKey =>
        `revenue_period_${++interval as YearPeriodNumber}`,
    // Margin columns are named gross_margin_period_3, gross_margin_period_4, etc.
    getGrossMarginColumnNameByInterval: (interval: YearPeriodInterval): GrossMarginYearPeriodKey =>
        `gross_margin_period_${++interval as YearPeriodNumber}`,
    // Margin percentage columns are named gross_margin_percentage_period_3, gross_margin_percentage_period_4, etc.
    getGrossMarginPercentageColumnNameByInterval: (
        interval: YearPeriodInterval,
    ): GrossMarginPercentageYearPeriodKey =>
        `gross_margin_percentage_period_${++interval as YearPeriodNumber}`,
};

export const getRevenueAndGrossMarginSelects = (from: Date, to: Date) => {
    const oldrColumn = <K extends keyof OrderAndInvoiceLineDailyRevenue>(col: K) => `oldr.${col}` as const;

    const revenue = sql.ref(oldrColumn('daily_revenue'));
    const grossMargin = sql.ref(oldrColumn('daily_gross_margin'));
    const units = sql.ref(oldrColumn('daily_units'));

    return [
        // Revenues
        sql<number>`SUM(CASE WHEN DATE(date) BETWEEN DATE(${from}) AND DATE(${to}) THEN ${revenue} ELSE 0 END)`.as(
            'revenue_current',
        ),
        sql<number>`SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 1 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 1 YEAR) THEN ${revenue} ELSE 0 END)`.as(
            'revenue_last_period',
        ),
        sql<number>`SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 2 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 2 YEAR) THEN ${revenue} ELSE 0 END)`.as(
            'revenue_period_3',
        ),
        sql<number>`SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 3 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 3 YEAR) THEN ${revenue} ELSE 0 END)`.as(
            'revenue_period_4',
        ),
        sql<number>`SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 4 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 4 YEAR) THEN ${revenue} ELSE 0 END)`.as(
            'revenue_period_5',
        ),

        // Gross Margins
        sql<number>`SUM(CASE WHEN DATE(date) BETWEEN DATE(${from}) AND DATE(${to}) THEN ${grossMargin} ELSE 0 END)`.as(
            'gross_margin_current',
        ),
        sql<number>`SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 1 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 1 YEAR) THEN ${grossMargin} ELSE 0 END)`.as(
            'gross_margin_last_period',
        ),
        sql<number>`SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 2 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 2 YEAR) THEN ${grossMargin} ELSE 0 END)`.as(
            'gross_margin_period_3',
        ),
        sql<number>`SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 3 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 3 YEAR) THEN ${grossMargin} ELSE 0 END)`.as(
            'gross_margin_period_4',
        ),
        sql<number>`SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 4 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 4 YEAR) THEN ${grossMargin} ELSE 0 END)`.as(
            'gross_margin_period_5',
        ),

        // Gross Margin Percentages (GM / Revenue)
        sql<number>`COALESCE(SUM(CASE WHEN DATE(date) BETWEEN DATE(${from}) AND DATE(${to}) THEN ${grossMargin} ELSE 0 END) / NULLIF(SUM(CASE WHEN DATE(date) BETWEEN DATE(${from}) AND DATE(${to}) THEN ${revenue} ELSE 0 END), 0), 0)`.as(
            'gross_margin_percentage_current',
        ),
        sql<number>`COALESCE(SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 1 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 1 YEAR) THEN ${grossMargin} ELSE 0 END) / NULLIF(SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 1 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 1 YEAR) THEN ${revenue} ELSE 0 END), 0), 0)`.as(
            'gross_margin_percentage_last_period',
        ),
        sql<number>`COALESCE(SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 2 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 2 YEAR) THEN ${grossMargin} ELSE 0 END) / NULLIF(SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 2 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 2 YEAR) THEN ${revenue} ELSE 0 END), 0), 0)`.as(
            'gross_margin_percentage_period_3',
        ),
        sql<number>`COALESCE(SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 3 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 3 YEAR) THEN ${grossMargin} ELSE 0 END) / NULLIF(SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 3 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 3 YEAR) THEN ${revenue} ELSE 0 END), 0), 0)`.as(
            'gross_margin_percentage_period_4',
        ),
        sql<number>`COALESCE(SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 4 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 4 YEAR) THEN ${grossMargin} ELSE 0 END) / NULLIF(SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 4 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 4 YEAR) THEN ${revenue} ELSE 0 END), 0), 0)`.as(
            'gross_margin_percentage_period_5',
        ),

        // Deltas
        sql<number>`(SUM(CASE WHEN DATE(date) BETWEEN DATE(${from}) AND DATE(${to}) THEN ${revenue} ELSE 0 END) - SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 1 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 1 YEAR) THEN ${revenue} ELSE 0 END))`.as(
            'revenue_delta_amount',
        ),
        sql<
            number | null
        >`(SUM(CASE WHEN DATE(date) BETWEEN DATE(${from}) AND DATE(${to}) THEN ${revenue} ELSE 0 END) - SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 1 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 1 YEAR) THEN ${revenue} ELSE 0 END)) / NULLIF(SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 1 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 1 YEAR) THEN ${revenue} ELSE 0 END), 0)`.as(
            'revenue_delta_percentage',
        ),

        sql<number>`(SUM(CASE WHEN DATE(date) BETWEEN DATE(${from}) AND DATE(${to}) THEN ${grossMargin} ELSE 0 END) - SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 1 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 1 YEAR) THEN ${grossMargin} ELSE 0 END))`.as(
            'gross_margin_delta_amount',
        ),
        sql<
            number | null
        >`(SUM(CASE WHEN DATE(date) BETWEEN DATE(${from}) AND DATE(${to}) THEN ${grossMargin} ELSE 0 END) - SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 1 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 1 YEAR) THEN ${grossMargin} ELSE 0 END)) / NULLIF(SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 1 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 1 YEAR) THEN ${grossMargin} ELSE 0 END), 0)`.as(
            'gross_margin_delta_percentage',
        ),

        // Units
        sql<number>`SUM(CASE WHEN DATE(date) BETWEEN DATE(${from}) AND DATE(${to}) THEN ${units} ELSE 0 END)`.as(
            'units_current',
        ),
        sql<number>`SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 1 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 1 YEAR) THEN ${units} ELSE 0 END)`.as(
            'units_last_period',
        ),

        sql<number>`(SUM(CASE WHEN DATE(date) BETWEEN DATE(${from}) AND DATE(${to}) THEN ${units} ELSE 0 END) - SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 1 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 1 YEAR) THEN ${units} ELSE 0 END))`.as(
            'units_delta_amount',
        ),
        sql<
            number | null
        >`(SUM(CASE WHEN DATE(date) BETWEEN DATE(${from}) AND DATE(${to}) THEN ${units} ELSE 0 END) - SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 1 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 1 YEAR) THEN ${units} ELSE 0 END)) / NULLIF(SUM(CASE WHEN DATE(date) BETWEEN DATE_SUB(DATE(${from}), INTERVAL 1 YEAR) AND DATE_SUB(DATE(${to}), INTERVAL 1 YEAR) THEN ${units} ELSE 0 END), 0)`.as(
            'units_delta_percentage',
        ),
    ];
};

export const getOrderLineStatusesForBillingStatus = (billingStatus: BillingStatus) => {
    if (billingStatus === BillingStatus.BILLED || billingStatus === BillingStatus.BILLED_WO_CREDIT_NOTE) {
        return [SrOrderLineStatus.BILLED, SrOrderLineStatus.PARTIALLY_BILLED];
    }
    return [
        SrOrderLineStatus.OPEN_IN_PICKING,
        SrOrderLineStatus.OPEN,
        SrOrderLineStatus.PARTIALLY_RECEIVED,
        SrOrderLineStatus.PARTIALLY_SHIPPED,
        SrOrderLineStatus.PENDING,
        SrOrderLineStatus.RECEIVED,
        SrOrderLineStatus.RELEASED_FOR_BILLING,
        SrOrderLineStatus.RELEASED_FOR_DELIVERY,
        SrOrderLineStatus.SHIPPED,
    ];
};

export const getOrderTypeForBillingStatus = (billingStatus: BillingStatus) => {
    if (billingStatus === BillingStatus.BILLED_WO_CREDIT_NOTE) {
        return [SrSalesOrderType.ORDER];
    }
    return [SrSalesOrderType.ORDER, SrSalesOrderType.CREDIT_NOTE];
};

export function getDateRangeForSparklines(): { from: Date; to: Date } {
    const now = new Date();
    const twoYearsAgo = new Date();
    twoYearsAgo.setFullYear(now.getFullYear() - 2);

    return {
        from: twoYearsAgo,
        to: now,
    };
}

export function create2YearMonthRange() {
    const endYear = new Date().getUTCFullYear();
    const startYear = endYear - 2;
    const endMonth = new Date().getMonth() + 1;
    const startMonth = endMonth;

    const startDate = new Date(startYear, startMonth - 1);
    const endDate = new Date(endYear, endMonth - 1);
    const dates = [];

    while (startDate <= endDate) {
        dates.push({
            year: startDate.getFullYear(),
            month: startDate.getMonth() + 1,
            monthYear: `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}`,
        });
        startDate.setMonth(startDate.getMonth() + 1);
    }

    return dates;
}

export type SparklineMonthGenerated = ReturnType<typeof create2YearMonthRange>[number];
export type SparklineMonth = SparklineMonthGenerated & {
    monthly_sales: number;
    monthly_order_count: number;
    monthly_unit_total: number;
};

export type MonthlySalesDataFromQuery = {
    year: number;
    month: number;
    month_year: string;
    monthly_sales: number;
    monthly_order_count: number;
    monthly_unit_total: number;
    [key: string]: any; // This allows for any number of additional properties with any key name
};

export function generateSparklineData(
    monthRange: SparklineMonthGenerated[],
    salesData: MonthlySalesDataFromQuery[],
): SparklineMonth[] {
    const monthsWithSalesData = monthRange.map(({ month, year, monthYear }) => {
        const salesDataForMonth = salesData.find((row) => row.month === month && row.year === year);
        return {
            month,
            year,
            monthYear,
            monthly_sales: salesDataForMonth?.monthly_sales ? Number(salesDataForMonth?.monthly_sales) : 0,
            monthly_order_count: salesDataForMonth?.monthly_order_count
                ? Number(salesDataForMonth?.monthly_order_count)
                : 0,
            monthly_unit_total: salesDataForMonth?.monthly_unit_total
                ? Number(salesDataForMonth?.monthly_unit_total)
                : 0,
        };
    });

    return monthsWithSalesData;
}

export function extractSalesDataByKey<T extends MonthlySalesDataFromQuery, K extends keyof T>(
    data: T[],
    key: K,
    value: T[K],
): T[] {
    return data.filter((item) => item[key] === value);
}

export const applyExplorerInputAclRules = <I extends { filters: ExplorerViewFilters }>(
    { auth }: { auth: AuthClient },
    input: I,
): I => ({
    ...input,
    filters: input.filters.customer?.length
        ? input.filters
        : auth.validateFilters('SalesReport', input.filters),
});

export const explorerForInputSchema = z.object({
    hideUnselectedRows: z.boolean().nullish().default(false),
    searchString: z.string().nullish(),
    window: z.nativeEnum(VisibilityWindow),
    customDateRange: z
        .object({
            from: z.date(),
            to: z.date(),
        })
        .optional()
        .nullable(),
    billingStatus: z.nativeEnum(BillingStatus),
    limit: z.number().min(1).max(10000).default(50),
    cursor: z.number().nullish(),
    filters: explorerViewFiltersSchema,
    sorting: explorerSortSchema,
    yearPeriods: z.number().min(2).max(5).default(2),
});

export const explorerForCategoryArbitraryDepthInputSchema = explorerForInputSchema.extend({
    depth: z.number().min(0).max(10),
});

export const explorerForNormalisedCategoryInputSchema = explorerForInputSchema.extend({
    normalisedCategoryDepth: z.number().min(0),
});

export const shouldHideUnselectedRows = ({
    tab,
    defaultFilters,
}: {
    defaultFilters?: Partial<ExplorerViewFilters>;
    tab: ExplorerTopLevelEntity;
}) => {
    return defaultFilters && getExplorerFilterKeyByTabName(tab) in defaultFilters;
};
